import React, { useState, useEffect } from 'react';
import {
  Sparkles, Play, Loader, Zap, Copy, Check,
  Lock, Unlock, History, Plus, Clock, ChevronDown,
  RotateCcw, ExternalLink, Link as LinkIcon, FlaskConical
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ModelLogo from './ModelLogo';
import { trackEvent } from '../utils/GoogleAnalytics';
import { showToast } from './Toast';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// CONSTANTS
// ==========================================
const MIN_TONES = 2;
const MAX_TONES = 5;
const MAX_TEXT_LENGTH = 1000;

// Detect RTL
const isRTL = (text) => {
  if (!text) return false;
  const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
  return rtlChars.test(text.substring(0, 100));
};

// Format relative time
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Tone border-top color classes
const TONE_BORDER_TOP = {
  professional: 'border-t-blue-500',
  friendly: 'border-t-emerald-500',
  angry: 'border-t-red-500',
  funny: 'border-t-amber-500',
  philosophical: 'border-t-purple-500',
  dramatic: 'border-t-orange-500',
};

const TONE_TEXT = {
  professional: 'text-blue-600',
  friendly: 'text-emerald-600',
  angry: 'text-red-600',
  funny: 'text-amber-600',
  philosophical: 'text-purple-600',
  dramatic: 'text-orange-600',
};

// ==========================================
// TONE RESULT CARD
// ==========================================
function ToneResultCard({ result, index }) {
  const [copied, setCopied] = useState(false);
  const borderTop = TONE_BORDER_TOP[result.tone_key] || 'border-t-gray-400';
  const textColor = TONE_TEXT[result.tone_key] || 'text-gray-600';

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(result.output);
    setCopied(true);
    showToast('Copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.4 }}
      className={`bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden border-t-4 ${borderTop} flex flex-col`}
    >
      {/* Tone Name + Copy */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{result.tone_emoji}</span>
          <span className={`font-bold text-sm ${textColor}`}>{result.tone_label}</span>
        </div>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
            copied
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
          }`}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Output */}
      <div className="px-4 pb-3 flex-1">
        <p
          className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap"
          dir={isRTL(result.output) ? 'rtl' : 'ltr'}
          style={{ textAlign: isRTL(result.output) ? 'right' : 'left' }}
        >
          {result.output}
        </p>
      </div>

      {/* Metrics Footer - fixed at bottom */}
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400 mt-auto">
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {(result.latency_ms / 1000).toFixed(1)}s
        </span>
        <span className="flex items-center gap-1">
          <Zap size={12} />
          {result.tokens} tokens
        </span>
        {result.error && (
          <span className="text-red-400">⚠️ {result.error}</span>
        )}
      </div>
    </motion.div>
  );
}
// ==========================================
// TONE SELECTOR
// ==========================================
function ToneSelector({ availableTones, selectedTones, onToggle }) {
  const handleToggle = (toneKey) => {
    if (selectedTones.includes(toneKey)) {
      onToggle(selectedTones.filter(t => t !== toneKey));
    } else {
      if (selectedTones.length >= MAX_TONES) {
        showToast(`Maximum ${MAX_TONES} tones allowed`, 'warning');
        return;
      }
      onToggle([...selectedTones, toneKey]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {availableTones.map(tone => {
          const selected = selectedTones.includes(tone.key);
          return (
            <button
              key={tone.key}
              onClick={() => handleToggle(tone.key)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                selected
                  ? 'border-rose-500 bg-rose-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {/* Checkbox */}
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                selected ? 'bg-rose-500 text-white' : 'border-2 border-gray-300'
              }`}>
                {selected && (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* Emoji */}
              <span className="text-xl flex-shrink-0">{tone.emoji}</span>

              {/* Name */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{tone.label}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Count */}
      <div className="flex items-center justify-between text-sm">
        <span className={`${selectedTones.length >= MIN_TONES ? 'text-rose-600' : 'text-orange-500'}`}>
          {selectedTones.length} of {MIN_TONES}-{MAX_TONES} tones selected
        </span>
        {selectedTones.length > 0 && (
          <button onClick={() => onToggle([])} className="text-red-500 hover:text-red-600 text-xs">
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

// ==========================================
// HISTORY CARD (for History tab)
// ==========================================
function HistoryCard({ run, onClick, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.35 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer p-4"
      onClick={() => onClick(run.id)}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <p
          className="text-sm font-medium text-gray-800 line-clamp-2 flex-1"
          dir={isRTL(run.original_text) ? 'rtl' : 'ltr'}
          style={{ textAlign: isRTL(run.original_text) ? 'right' : 'left' }}
        >
          {run.original_text}
        </p>
        {run.is_public ? (
          <Unlock size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <Lock size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
        )}
      </div>

      {/* Tone emojis + model */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {run.results?.map((r, i) => (
          <span key={i} className="bg-rose-50 rounded-full px-2 py-0.5 text-xs">
            {r.tone_emoji} {r.tone_label}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <ModelLogo modelName={run.model_name} size="xs" />
          <span>{run.model_name}</span>
        </div>
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {formatTimeAgo(run.created_at)}
        </span>
      </div>
    </motion.div>
  );
}

// ==========================================
// MAIN TONELAB COMPONENT
// ==========================================
function ToneLab({ user, onCreditsUpdate }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'history';

  // State
  const [activeTab, setActiveTab] = useState(initialTab);
  const [text, setText] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedTones, setSelectedTones] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [runId, setRunId] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState('');

  // Data loading
  const [models, setModels] = useState([]);
  const [tones, setTones] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [expandedProvider, setExpandedProvider] = useState(null);

  // History
  const [historyRuns, setHistoryRuns] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'public' | 'mine'

  const token = localStorage.getItem('token');

  // Load models and tones
  useEffect(() => {
    const loadData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // STATIC MODE: load from local JSON files
        const [modelsRes, tonesRes] = await Promise.all([
          fetch('/data/models.json'),
          fetch('/data/tones.json')
        ]);

        if (modelsRes.ok) {
          const allModels = await modelsRes.json();
          const m = allModels.filter(model => model.category === 'CHAT' && model.is_active);
          setModels(m);
          const firstModel = m[0]; // PORTFOLIO: allow any model
          if (firstModel) setSelectedModel(firstModel);
        }

        if (tonesRes.ok) {
          setTones(await tonesRes.json());
        }
      } catch (e) {
        console.error('Failed to load AI Tone data:', e);
      } finally {
        setModelsLoading(false);
      }
    };
    loadData();
  }, [token]);

  // Load history when tab or filter changes
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, historyFilter]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      let runs = [];
      let total = 0;

      // STATIC MODE: load from local JSON file
      const res = await fetch('/data/tonelab.json');
      if (res.ok) {
        const data = await res.json();
        runs = data.runs || [];
        total = data.total || runs.length;
      }

      setHistoryRuns(runs);
      setHistoryTotal(total);
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Calculate cost
  const creditsPerRun = selectedModel?.credits_per_run || 1;
  const totalCost = creditsPerRun * selectedTones.length;
  const userCredits = user?.credits || 0;
  const canRun = user && selectedModel && selectedTones.length >= MIN_TONES && text.trim().length >= 5 && userCredits >= totalCost;

  // Run ToneLab
  const handleRun = async () => {
    if (!canRun) return;
    setError('');
    setIsRunning(true);
    setResults(null);

    try {
      const res = await fetch(`${API_URL}/tonelab/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text.trim(),
          model_id: selectedModel.id,
          tones: selectedTones
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to run AI Tone');
      }

      const data = await res.json();
      setResults(data.results);
      setRunId(data.id);
      setIsPublic(data.is_public);

      if (onCreditsUpdate) onCreditsUpdate();

      showToast(`🧪 AI Tone complete! ${data.results.length} tones generated`, 'success');
      trackEvent('ToneLab', 'Run', selectedModel?.name, selectedTones.length);
    } catch (e) {
      setError(e.message);
      showToast(e.message, 'error');
    } finally {
      setIsRunning(false);
    }
  };

  // Toggle public/private
  const handleTogglePublic = async () => {
    if (!runId) return;
    try {
      const res = await fetch(`${API_URL}/tonelab/view/${runId}/toggle-public`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsPublic(data.is_public);
        showToast(data.is_public ? '🌐 Made public!' : '🔒 Made private', 'success');
      }
    } catch (e) {
      showToast('Failed to update visibility', 'error');
    }
  };

  // Copy share link
  const handleCopyLink = () => {
    const url = `${window.location.origin}/tonelab/${runId}`;
    navigator.clipboard.writeText(url);
    showToast('Link copied!', 'success');
  };

  // Reset
  const handleReset = () => {
    setResults(null);
    setRunId(null);
    setIsPublic(false);
    setError('');
  };

  return (
    <div className="min-h-screen">
      {/* Floating Header with Tabs - same pattern as GroupChat */}
      {!results && (
        <div className="px-4 sm:px-0 pb-4">
          <div className="max-w-6xl mx-auto px-0 sm:px-6">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-sm sm:shadow-md overflow-hidden">
              <div className="px-4 sm:px-6 py-4">
                {/* Page Title */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-rose-400 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                    <FlaskConical size={20} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">AI Tone</h1>
                    <p className="text-sm text-gray-500">Same text, different vibes</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2" role="tablist" aria-label="Tone navigation">
                  <button
                    onClick={() => setActiveTab('history')}
                    role="tab"
                    aria-selected={activeTab === 'history'}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 'history'
                        ? 'bg-rose-100 text-rose-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <History size={18} />
                    {user ? 'History' : 'Recent'}
                    {historyTotal > 0 && (
                      <span className="bg-rose-500 text-white px-2 py-0.5 rounded-full text-xs">
                        {historyTotal}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('new')}
                    role="tab"
                    aria-selected={activeTab === 'new'}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 'new'
                        ? 'bg-rose-100 text-rose-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Plus size={18} />
                    New Tone
                  </button>
                </div>

                {/* Login prompt */}
                {!user && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                    <span className="text-orange-500 text-sm">⚠️ Please login to use AI Tone</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 sm:px-0">
      <div className="max-w-6xl mx-auto px-0 sm:px-6 pb-8">

      {/* HISTORY TAB */}
      {activeTab === 'history' && !results && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-2 shadow-sm">
            <div className="flex items-center gap-1">
              {[
                { id: 'all', label: 'All Results' },
                { id: 'public', label: 'Public' },
                ...(user ? [{ id: 'mine', label: 'My Tones' }] : [])
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setHistoryFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    historyFilter === f.id
                      ? 'bg-rose-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {historyTotal > 0 && (
              <span className="text-xs text-gray-400 pr-2">{historyTotal} results</span>
            )}
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="animate-spin text-rose-500" size={32} />
            </div>
          ) : historyRuns.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <History size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700">
                {historyFilter === 'mine' ? 'No tones yet' : 'No public tones yet'}
              </h3>
              <p className="text-gray-500 mt-1">
                {historyFilter === 'mine' ? 'Try your first AI Tone rewrite' : 'Be the first to create a public tone!'}
              </p>
              <button
                onClick={() => setActiveTab('new')}
                className="mt-4 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
              >
                New Tone
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {historyRuns.map((run, i) => (
                <HistoryCard key={run.id} run={run} index={i} onClick={(id) => navigate(`/tonelab/${id}`)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* NEW TAB - Setup (shown when no results yet) */}
      {activeTab === 'new' && !results && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">

          {/* Text Input */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles size={18} className="text-rose-500" />
              Your Text
            </h2>
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setError(''); }}
              placeholder="Write or paste your text here... (e.g., 'I need to tell my boss I deserve a raise')"
              className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
              rows={4}
              maxLength={MAX_TEXT_LENGTH}
              dir={isRTL(text) ? 'rtl' : 'ltr'}
            />
            <div className="text-xs text-gray-400 text-right mt-1">{text.length}/{MAX_TEXT_LENGTH}</div>
          </div>

          {/* Model Selection - Accordion by Provider */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Zap size={18} className="text-rose-500" />
              AI Model
              {selectedModel && (
                <span className="text-xs text-gray-400 font-normal ml-auto">{selectedModel.name} • {selectedModel.credits_per_run} cr/tone</span>
              )}
            </h2>

            {modelsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="animate-spin text-rose-500" size={20} />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Provider Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {(() => {
                    const grouped = {};
                    models.forEach(model => {
                      const provider = model.provider || 'Other';
                      if (!grouped[provider]) grouped[provider] = [];
                      grouped[provider].push(model);
                    });
                    // Sort: provider with selected model first
                    const providers = Object.keys(grouped).sort((a, b) => {
                      const aHas = grouped[a].some(m => selectedModel?.id === m.id);
                      const bHas = grouped[b].some(m => selectedModel?.id === m.id);
                      if (aHas && !bHas) return -1;
                      if (!aHas && bHas) return 1;
                      return a.localeCompare(b);
                    });

                    return providers.map(provider => {
                      const providerModels = grouped[provider];
                      const hasSelected = providerModels.some(m => selectedModel?.id === m.id);
                      const isExpanded = expandedProvider === provider;

                      return (
                        <button
                          key={provider}
                          onClick={() => setExpandedProvider(isExpanded ? null : provider)}
                          className={`relative flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${
                            isExpanded
                              ? 'bg-rose-50 border-rose-400 shadow-md'
                              : hasSelected
                                ? 'bg-white border-rose-300 shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <ModelLogo modelName={providerModels[0]?.name || provider} size="sm" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-gray-900 truncate">{provider}</div>
                            <div className="text-xs text-gray-400">{providerModels.length} model{providerModels.length > 1 ? 's' : ''}</div>
                          </div>
                          {hasSelected && (
                            <span className="bg-rose-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">✓</span>
                          )}
                          <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      );
                    });
                  })()}
                </div>

                {/* Expanded Models Panel */}
                {expandedProvider && (() => {
                  const grouped = {};
                  models.forEach(model => {
                    const provider = model.provider || 'Other';
                    if (!grouped[provider]) grouped[provider] = [];
                    grouped[provider].push(model);
                  });
                  const providerModels = grouped[expandedProvider];
                  if (!providerModels) return null;

                  // Sort by flagship_score descending (most advanced first)
                  const sorted = [...providerModels].sort((a, b) => (b.flagship_score || 0) - (a.flagship_score || 0));

                  return (
                    <div className="bg-rose-50 rounded-xl border border-rose-200 p-3 space-y-1.5">
                      <span className="text-xs font-bold text-rose-600 uppercase tracking-wider">{expandedProvider} Models</span>
                      {sorted.map(model => {
                        const isActive = selectedModel?.id === model.id;
                        return (
                          <button
                            key={model.id}
                            onClick={() => {
                              // PORTFOLIO: locked check removed — allow selecting in demo mode
                              setSelectedModel(model);
                              setExpandedProvider(null);
                            }}
                            disabled={false}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                              isActive ? 'bg-white shadow-sm ring-2 ring-rose-300' : 'hover:bg-white/80'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                              isActive ? 'bg-rose-500 border-transparent text-white' : 'border-gray-300 bg-white'
                            }`}>
                              {isActive && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <ModelLogo modelName={model.name} size="sm" />
                            <div className="flex-1 min-w-0">
                              <span className={`font-medium text-sm ${model.locked ? 'text-gray-400' : 'text-gray-900'}`}>{model.name}</span>
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0">{model.credits_per_run} cr</span>
                            {model.locked && <span className="text-orange-500 flex-shrink-0">🔒</span>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Tone Selection */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-rose-500">🎭</span>
              Select Tones ({MIN_TONES}-{MAX_TONES})
            </h2>

            <ToneSelector
              availableTones={tones}
              selectedTones={selectedTones}
              onToggle={setSelectedTones}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Credits Info */}
          <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Zap size={16} className="text-yellow-500" />
              <span>
                {selectedTones.length > 0 && selectedModel
                  ? `${selectedTones.length} tones × ${creditsPerRun} credits = ${totalCost} credits`
                  : 'Select model and tones to see cost'
                }
              </span>
            </div>
            <div className="text-sm font-semibold">
              Your credits: <span className={`${userCredits >= totalCost ? 'text-green-600' : 'text-red-500'}`}>{userCredits}</span>
            </div>
          </div>

          {/* Run Button */}
          <button
            onClick={handleRun}
            disabled={true}
            className="w-full py-4 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg"
            title="Demo mode — running disabled"
          >
            <>
              <Sparkles size={20} />
              Remix It!
            </>
          </button>
        </div>
      )}

      {/* RESULTS */}
      {results && (
        <div className="space-y-6">
          {/* Results Header - Clean white card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-rose-400 to-pink-500" />
            <div className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 text-gray-500 text-sm">
                    <FlaskConical size={16} className="text-rose-500" />
                    <span className="font-medium text-gray-900">AI Tone</span>
                  </div>
                  <p
                    className="font-medium text-gray-900 text-sm"
                    dir={isRTL(text) ? 'rtl' : 'ltr'}
                    style={{ textAlign: isRTL(text) ? 'right' : 'left' }}
                  >
                    {text}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    {selectedModel && (
                      <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-2.5 py-1">
                        <ModelLogo modelName={selectedModel.name} size="xs" />
                        <span className="text-sm text-gray-700">{selectedModel.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm text-gray-700"
                >
                  <RotateCcw size={16} />
                  New
                </button>
              </div>
            </div>
          </div>

          {/* Tone Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AnimatePresence>
              {results.map((result, i) => (
                <ToneResultCard key={result.tone_key} result={result} index={i} />
              ))}
            </AnimatePresence>
          </div>

          {/* Make Public Prompt - animated lock */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className={`rounded-xl p-4 border ${
              isPublic
                ? 'bg-green-50 border-green-200'
                : 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200'
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <motion.div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isPublic ? 'bg-green-100' : 'bg-rose-100'
                  }`}
                  animate={!isPublic ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                  {isPublic
                    ? <Unlock size={20} className="text-green-600" />
                    : <Lock size={20} className="text-rose-600" />
                  }
                </motion.div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    {isPublic ? 'This tone remix is public ✨' : 'Share your tone remix?'}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {isPublic ? 'Anyone with the link can see it' : 'Make it public so others can discover it'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isPublic && (
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-50 transition-all border border-rose-200"
                  >
                    <LinkIcon size={14} />
                    Copy Link
                  </button>
                )}
                <motion.button
                  onClick={handleTogglePublic}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    isPublic
                      ? 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      : 'bg-rose-500 hover:bg-rose-600 text-white shadow-md'
                  }`}
                >
                  {isPublic ? <Lock size={16} /> : <Unlock size={16} />}
                  <span>{isPublic ? 'Make Private' : 'Make Public'}</span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* View Detail Link */}
          <div className="flex justify-end">
            <button
              onClick={() => navigate(`/tonelab/${runId}`)}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm transition-all"
            >
              <ExternalLink size={14} />
              View full page
            </button>
          </div>
        </div>
      )}

      </div>
      </div>
    </div>
  );
}

export default ToneLab;